"use client";
import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import NextImage from "next/image";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Link from "@AdbhutByte/utils/components/Link";
import Tooltip from "@mui/material/Tooltip";
import { usePathname } from "next/navigation";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import WavingHandIcon from "@mui/icons-material/WavingHand";
import getProfilesToFollow, {
  profileProps,
} from "@AdbhutByte/utils/lib/getProfilesToFollow";
import Grid from "@mui/material/Grid";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import Slide from "@mui/material/Slide";
import { close as requestCVDialogueClose } from "@AdbhutByte/utils/config/ui/state/slices/requestCVDialogueSlice";
import {
  open as socialDialogueOpen,
  close as socialDialogueClose,
} from "@AdbhutByte/utils/config/ui/state/slices/socialDialogueSlice";
import {
  useAppDispatch,
  useAppSelector,
} from "@AdbhutByte/utils/functions/stateSelector";
import {
  SITE_FALLBACK_NAME,
  SITE_FALLBACK_URL,
} from "@AdbhutByte/utils/universalModifiers";

const Image = styled(NextImage)({});

const baseUrl = process.env.NEXT_PUBLIC_URL ?? SITE_FALLBACK_URL;
const baseName = process.env.NEXT_PUBLIC_SITE_NAME ?? SITE_FALLBACK_NAME;

type navItemsProps = Array<{
  label: string;
  link: Array<string> | string;
  tooltip: string | false;
  visibleIn: false | string | Array<string>;
  visibleExcept: false | string | Array<string>;
  hideSelf: boolean;
}>;

interface NavigationProps {
  window?: () => Window;
}

export default function Navigation(navigationProps: NavigationProps) {
  const { window } = navigationProps;

  const socialDialogueState = useAppSelector(
    (state) => state.socialDialogueReducer.value
  );
  const dispatch = useAppDispatch();
  const [profiles, setProfiles] = React.useState<profileProps[] | false>(false);

  const pathname = usePathname();

  const handleSocialDialogOpen = () => {
    dispatch(socialDialogueOpen());
    dispatch(requestCVDialogueClose());
  };

  const handleSocialDialogClose = () => {
    dispatch(socialDialogueClose());
    dispatch(requestCVDialogueClose());
  };

  const triggerElevate = useScrollTrigger({
    disableHysteresis: true,
    threshold: 150,
    target: window ? window() : undefined,
  });

  const triggerHide = useScrollTrigger({
    threshold: 500,
    target: window ? window() : undefined,
  });

  const triggerFixed = useScrollTrigger({
    disableHysteresis: true,
    threshold: 200,
    target: window ? window() : undefined,
  });

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const profilesGet = await getProfilesToFollow();
        setProfiles(profilesGet as profileProps[]);
      } catch (error) {
        setProfiles(false);
      }
    };

    fetchData();
  }, []);

  if (profiles) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Slide appear={false} direction="down" in={!triggerHide}>
          <AppBar
            position={!triggerFixed ? "absolute" : "fixed"}
            elevation={triggerElevate ? 4 : 0}
          >
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ verticalAlign: "middle" }}
                >
                  <Link
                    href="/"
                    color="inherit"
                    sx={{ textDecoration: "none" }}
                  >
                    <Typography variant="inherit">{baseName}</Typography>
                  </Link>
                </Stack>
              </Typography>
              {pathname.split("/")[1].toLowerCase() !== "social" ? (
                <Tooltip title="Connect with me">
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={handleSocialDialogOpen}
                  >
                    <WavingHandIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Toolbar>
          </AppBar>
        </Slide>
        <Dialog
          maxWidth="xs"
          open={socialDialogueState}
          onClose={handleSocialDialogClose}
          aria-labelledby="social-dialog"
        >
          <DialogTitle>Hey! He&apos;s here too</DialogTitle>
          <DialogContent>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              {profiles
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((profile, index) => {
                  if (!profile.visible) return;
                  return (
                    <Grid item key={index}>
                      <Box
                        component={Link}
                        href={`/${profile.name as string}`}
                        sx={{ m: 0, p: 0 }}
                        onClick={handleSocialDialogClose}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column"
                      >
                        <Image
                          src={profile.icon as string}
                          alt={baseName + " on " + profile.brand_name}
                          width={50}
                          height={50}
                          sx={{ textAlign: "center", objectFit: "contain" }}
                        />
                        <Typography
                          textTransform="capitalize"
                          variant="caption"
                          color="text.secondary"
                        >
                          {profile.brand_name}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSocialDialogClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}
